// @flow
import React, { useContext } from 'react';
import {
  compose,
  withProps,
  setDisplayName,
  withHandlers,
  branch,
} from 'recompose';
import reactCSS from 'reactcss';
import {
  Field,
  reduxForm,
  formValues as reduxFormValues,
} from 'redux-form';
import { ReduxFormContext } from 'redux-form/es/ReduxFormContext';
import { injectIntl, FormattedMessage } from 'react-intl';
import FilterTLD from './TLD';
import FilterSwitch from './Switch';
import { getStyles } from '@ux/style-utils';
import { gu2px } from '@ux/theme-utils';
import { FQDN_MAX_LENGTH } from '../../../domain/filter';
import { tldToFormName } from '../../../domain/tld/utils';
import { isFalse, whenTrue } from '../../../crosscutting/capra';
import * as r from 'ramda';

const stylesheet = ({
  theme,
}) => {
  const {
    spacing,
  } = theme;

  return {
    default: {
      unspin: {
        marginBottom: gu2px(2, spacing),
      },
      tlds: {
        opacity: 1,
        transition: 'opacity 0.3s ease-in-out',
      },
    },

    isUnspun: {
      tlds: {
        opacity: 0,
        height: 0,
        pointerEvents: 'none',
      },
    },

    isMinSm: {
      unspin: {
        marginBottom: gu2px(1, spacing),
      },
    },

    isMinMd: {
      unspin: {
        marginBottom: 0,
        flex: 1,
        flexBasis: 'auto',
        marginRight: gu2px(1, spacing),
      },
    },

    canUnspinMd: {
      row: {
        display: 'flex',
      },
    },
  };
};

const stylesheetToStyles = (stylesheet, props) => reactCSS(stylesheet, {
  isMinSm: props.isMinSm,
  isMinMd: props.isMinMd,
  isUnspun: props.isUnspun, // from Connected
  canUnspinMd: props.canUnspin && props.isMinMd,
});

const formValues = (args) => (Component) => {
  const Enhanced = reduxFormValues(args)(Component);
  return (props) => {
    const context = useContext(ReduxFormContext);
    return (
      <Enhanced
        {...props}
        _reduxForm={context}
      />
    );
  };
};

export const PureFilters = ({
  styles,
  theme,
  form,
  tlds,
  allChecked,
  isMinMd,
  isMinLg,
  //features
  canUnspin,
  // Events
  onAllTldsChange,
}) => (
  <form onSubmit={form.handleSubmit}>
    <fieldset>
      <div style={styles.row}>
        {whenTrue(
          () => (
            <div style={styles.unspin}>
              <Field
                name="unspun"
                theme={theme}
                component={FilterSwitch}
                pin="dstFiltersUnspun"
                value="unspun"
                isMinMd={isMinMd}
                isMinLg={isMinLg}
                label={(<FormattedMessage id="dst.filters.exactOnly"/>)}
              />
            </div>
          )
        )(canUnspin)}

        <div style={styles.tlds}>
          <FilterTLD
            theme={theme}
            tlds={tlds}
            allChecked={allChecked}
            isMinMd={isMinMd}
            isMinLg={isMinLg}
            onAllTldsChange={onAllTldsChange}
          />
        </div>
      </div>
    </fieldset>
  </form>
);

export const enhance = compose(
  setDisplayName('Filters'),
  withHandlers({
    onChange: () => (values, dispatch, form) => form.submit(),
  }),
  withProps((ownerProps) => ({
    initialValues: r.merge({
      length: FQDN_MAX_LENGTH,
    }, ownerProps.initialFilters),
  })),
  reduxForm({
    form: 'domainFilters',
    propNamespace: 'form',
    shouldAsyncValidate: r.T,
    destroyOnUnmount: false,
  }),
  formValues({
    tldValues: 'tld',
  }),
  injectIntl,
  withProps((ownerProps) => {
    const canUnspin = r.pathEq([ 'features', 'canUnspin' ], true)(ownerProps);
    const allChecked = r.pipe(
      r.ifElse(
        r.isNil,
        r.T,
        r.pipe(
          r.values,
          r.all(isFalse),
        ),
      ),
    )(ownerProps.tldValues);

    const styles = getStyles(stylesheet, stylesheetToStyles, {
      ...ownerProps,
      canUnspin,
    });

    return {
      canUnspin,
      allChecked,
      styles,
    };
  }),
  withHandlers({
    onResetClick: ({
      form,
    }) => () => form.reset(),
    onAllTldsChange: ({
      form,
      tlds,
    }) => () => {
      // @TODO: Move this out into an epic
      r.forEach((tld) => {
        form.change(`tld.${tldToFormName(tld)}`, false);
      })(tlds);
    },
  }),
  branch(
    r.complement(r.prop('showFilters')),
    r.always(r.always(null)),
  ),
);

export default enhance(PureFilters);
